import { Box, Checkbox, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded';
import SwapHorizRounded from '@mui/icons-material/SwapHorizRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import PersonRounded from '@mui/icons-material/PersonRounded';
import FolderRounded from '@mui/icons-material/FolderRounded';
import CreditCardRounded from '@mui/icons-material/CreditCardRounded';
import AttachFileRounded from '@mui/icons-material/AttachFileRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import PaymentsRounded from '@mui/icons-material/PaymentsRounded';
import type { BulkSelect } from '../../hooks/useBulkSelect';
import type { ViewMode } from '../ui/ViewToggle';
import type { PaymentItem } from '../../services/api';
import NoteStyleCard, { GlassIconTile } from '../ui/NoteStyleCard';

const TYPE = {
  income: { label: 'Income', Icon: ArrowDownwardRounded, color: '#0F9D58', accent2: '#38EF7D', sign: '+' },
  expense: { label: 'Expense', Icon: ArrowUpwardRounded, color: '#DC2626', accent2: '#F45C43', sign: '-' },
  transfer: { label: 'Transfer', Icon: SwapHorizRounded, color: '#2563EB', accent2: '#60A5FA', sign: '' },
} as const;

const CATEGORY_COLORS: Record<string, string> = {
  Utilities: '#EF4444', Food: '#F59E0B', Shopping: '#6366F1', Travel: '#06B6D4', Health: '#EC4899',
  Education: '#8B5CF6', Entertainment: '#F97316', Bills: '#0EA5E9', Salary: '#22C55E', Other: '#64748B',
};

const fmtMoney = (n: number) => `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtDate = (v: string) =>
  new Date(v).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

type Props = {
  items: PaymentItem[];
  bulk: BulkSelect;
  view: ViewMode;
  onEdit: (p: PaymentItem) => void;
  onDelete: (p: PaymentItem) => void;
};

/** Payments in the same card style as Notes: gradient header (type colour) + white body. */
const PaymentCards = ({ items, bulk, view, onEdit, onDelete }: Props) => {
  const grid = view === 'grid';

  return (
    <Box
      sx={grid
        ? { display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))', lg: 'repeat(3, minmax(0,1fr))' } }
        : { display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      {items.map((p, idx) => {
        const t = TYPE[p.type];
        const pending = p.status === 'pending';
        const catColor = (p.category && CATEGORY_COLORS[p.category]) || '#64748B';
        const selected = bulk.isSelected(p._id);

        const amountBlock = (
          <Box sx={{ textAlign: grid ? 'right' : 'center', minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 900, fontSize: grid ? 24 : 18, lineHeight: 1.15, letterSpacing: -0.3,
                textShadow: '0 2px 8px rgba(0,0,0,.25)',
              }}
              noWrap
            >
              {t.sign}{fmtMoney(p.amount)}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 800, opacity: 0.95 }}>
              {t.label}
            </Typography>
          </Box>
        );

        return (
          <NoteStyleCard
            key={p._id}
            accent={t.color}
            accent2={t.accent2}
            grid={grid}
            index={idx}
            selected={selected}
            watermark={PaymentsRounded}
            header={
              <>
                <GlassIconTile size={grid ? 46 : 52}><t.Icon /></GlassIconTile>
                {amountBlock}
              </>
            }
          >
            <Stack direction="row" alignItems="flex-start" spacing={1}>
              <Checkbox size="small" checked={selected} onChange={() => bulk.toggle(p._id)} sx={{ p: 0.25, ml: -0.5 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 800, flex: 1, minWidth: 0, lineHeight: 1.3 }} noWrap title={p.title}>
                {p.title}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', pt: 0.5 }}>
                {fmtDate(p.date)}
              </Typography>
            </Stack>

            {p.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden' }}
              >
                {p.description}
              </Typography>
            )}

            <Box sx={{ flex: 1 }} />

            <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
              <Chip
                size="small"
                icon={pending ? <ScheduleRounded sx={{ fontSize: 15 }} /> : <CheckCircleRounded sx={{ fontSize: 15 }} />}
                label={pending ? 'Pending' : 'Completed'}
                sx={{
                  fontWeight: 800,
                  bgcolor: pending ? '#F59E0B22' : '#16A34A1F',
                  color: pending ? '#B45309' : '#15803D',
                  '& .MuiChip-icon': { color: 'inherit' },
                }}
              />
              {p.category && (
                <Chip size="small" label={p.category} sx={{ bgcolor: `${catColor}1F`, color: catColor, fontWeight: 800 }} />
              )}
              {p.person && <Chip size="small" icon={<PersonRounded sx={{ fontSize: 15 }} />} label={p.person} variant="outlined" />}
              {p.project?.name && <Chip size="small" icon={<FolderRounded sx={{ fontSize: 15 }} />} label={p.project.name} variant="outlined" />}
              {p.method && <Chip size="small" icon={<CreditCardRounded sx={{ fontSize: 15 }} />} label={p.method} variant="outlined" />}
              {!!p.receipts?.length && (
                <Chip size="small" icon={<AttachFileRounded sx={{ fontSize: 15 }} />} label={p.receipts.length} variant="outlined" />
              )}
            </Stack>

            <Stack direction="row" justifyContent="flex-end" spacing={0.25}>
              <Tooltip title="Edit"><IconButton size="small" onClick={() => onEdit(p)}><EditRounded fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => onDelete(p)}><DeleteRounded fontSize="small" /></IconButton></Tooltip>
            </Stack>
          </NoteStyleCard>
        );
      })}
    </Box>
  );
};

export default PaymentCards;
